import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import {
  brokers,
  members,
  roles,
  type GrantableRole,
  type InvitationIssued,
  type Member,
} from '@/shared/api/resources';
import { useAuth } from '@/shared/auth/useAuth';
import { formatDateTime } from '@/shared/lib/format';
import { Alert } from '@/shared/ui/Alert';
import { Badge } from '@/shared/ui/Badge';
import { Button } from '@/shared/ui/Button';
import { Card } from '@/shared/ui/Card';
import { DataTable, type Column } from '@/shared/ui/DataTable';
import { EmptyState } from '@/shared/ui/EmptyState';
import { Field } from '@/shared/ui/Field';
import { ConfirmDialog, Modal } from '@/shared/ui/Modal';
import { PageHeader } from '@/shared/ui/PageHeader';
import { Select } from '@/shared/ui/Select';
import { Skeleton } from '@/shared/ui/Skeleton';
import { StatusBadge } from '@/shared/ui/StatusBadge';
import { Textarea } from '@/shared/ui/Textarea';
import { useToast } from '@/shared/ui/useToast';
import { classeDaAba, trilhoDeAbas } from '@/shared/ui/tabStyles';

import { formatarTelefone } from './formatoDoUsuario';
import { EnderecoDoConvite, InvitationsPanel } from './InvitationsPanel';
import { MenuDeAcoes, type AcaoDoMenu } from './MenuDeAcoes';
import { RoleBoards } from './RoleBoards';
import { UserFormModal, type ModoDoFormulario } from './UserFormModal';

/**
 * Usuarios e perfis.
 *
 * A tela administra quem tem acesso a empresa e o que cada pessoa pode fazer. Ela
 * segue o OnPoint (P-USU-1): as abas "Usuarios" e "Perfis de acesso", nessa ordem, e
 * a aba "Convites" do SeguraCore depois; a lista com Acoes, Nome, E-mail, Telefone,
 * Ativo e Perfil; a busca "Digite para buscar..."; os botoes Cadastrar Usuario e
 * Exportar CSV; e o menu da linha com Visualizar, Editar, Excluir e Ativar / inativar
 * usuario. O que o SeguraCore ja tinha continua: o ultimo acesso, a suspensao com
 * motivo, a corretora representada e mais de um perfil por pessoa.
 *
 * A busca acontece no servidor. Filtrar no navegador daria resposta errada assim que
 * a lista passasse de uma pagina, e a lista de pessoas cresce com a empresa.
 *
 * Para quem administra a corretora, a mesma tela mostra so as pessoas da corretora,
 * oferece so os dois perfis dela e nao tem perfis de acesso nem escolha de corretora:
 * o servidor ja recorta a lista e prende o convite a corretora da sessao.
 */

type Aba = 'usuarios' | 'perfis' | 'convites';

const situacoes = [
  { value: '', label: 'Todas as situacoes' },
  { value: 'active', label: 'Ativos' },
  { value: 'suspended', label: 'Suspensos' },
  { value: 'inactive', label: 'Inativos' },
];

type Formulario = { modo: ModoDoFormulario; membro?: Member };

export function UsersPage() {
  const { can, session } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();
  const location = useLocation();
  const navigate = useNavigate();

  const daCorretora = session?.broker_scoped === true;
  const podeVer = can('usuarios.usuario.view');
  const podeCadastrar = can('usuarios.usuario.create');
  const podeEditar = can('usuarios.usuario.update');
  const podeRemover = can('usuarios.usuario.delete');
  const podeExportar = can('usuarios.usuario.export');
  const podeMandarLink = can('usuarios.senha.update');
  // Quem administra a corretora escolhe o tipo de perfil como parte de administrar
  // o usuario. Para a seguradora, trocar perfil continua sendo conceder papel.
  const podeGerirPapeis = daCorretora ? podeEditar : can('usuarios.papel.update');
  const podeListarPerfis = daCorretora
    ? podeCadastrar || podeEditar
    : can('usuarios.papel.view');
  const veQuadros = !daCorretora && can('usuarios.papel.view');

  // /usuarios/perfis abre a aba de perfis, como a rota do OnPoint.
  const abaDaRota: Aba = location.pathname.endsWith('/perfis') ? 'perfis' : 'usuarios';
  const [aba, setAba] = useState<Aba>(abaDaRota);
  const [busca, setBusca] = useState('');
  const [buscaAplicada, setBuscaAplicada] = useState('');
  const [situacao, setSituacao] = useState('');
  const [corretora, setCorretora] = useState('');
  const [exportando, setExportando] = useState(false);

  const [formulario, setFormulario] = useState<Formulario | null>(null);
  const [emitido, setEmitido] = useState<InvitationIssued | null>(null);
  const [detalhe, setDetalhe] = useState<Member | null>(null);
  const [suspendendo, setSuspendendo] = useState<Member | null>(null);
  const [inativando, setInativando] = useState<Member | null>(null);
  const [reativando, setReativando] = useState<Member | null>(null);
  const [removendo, setRemovendo] = useState<Member | null>(null);
  const [mandandoLink, setMandandoLink] = useState<Member | null>(null);

  // A busca espera a digitacao parar. Uma consulta por tecla transformaria a lista em
  // uma sequencia de respostas fora de ordem, e a ultima a chegar nem sempre e a do
  // que esta escrito no campo.
  useEffect(() => {
    const relogio = setTimeout(() => setBuscaAplicada(busca.trim()), 300);
    return () => clearTimeout(relogio);
  }, [busca]);

  const filtros = { q: buscaAplicada, status: situacao, broker_id: corretora };

  const consulta = useQuery({
    queryKey: ['members', filtros],
    queryFn: ({ signal }) => members.list(filtros, signal),
    enabled: podeVer,
  });

  const listaPapeis = useQuery({
    queryKey: daCorretora ? ['roles', 'concediveis'] : ['roles'],
    queryFn: ({ signal }): Promise<GrantableRole[]> =>
      daCorretora ? members.grantableRoles(signal) : roles.list(signal),
    // A lista de perfis alimenta o formulario e a troca de perfil. A rota da
    // seguradora exige usuarios.papel.view, e pedi-la sem a permissao so produziria
    // um 403 silencioso e um formulario sem opcao nenhuma. A corretora pede os
    // perfis que pode conceder, que sao so os dois dela.
    enabled: podeListarPerfis,
  });

  const corretoras = useQuery({
    queryKey: ['brokers', 'options'],
    queryFn: ({ signal }) => brokers.list({ limit: 100, stage: 'ativo' }, signal),
    enabled: !daCorretora && can('corretores.corretor.view'),
  });

  function invalidar() {
    void queryClient.invalidateQueries({ queryKey: ['members'] });
    void queryClient.invalidateQueries({ queryKey: ['roles'] });
  }

  const trocarPapeis = useMutation({
    mutationFn: ({ memberId, roleIds }: { memberId: string; roleIds: string[] }) =>
      members.setRoles(memberId, roleIds),
    onSuccess: () => {
      toast.success('Acesso atualizado. Vale na proxima requisicao da pessoa.');
      setDetalhe(null);
      invalidar();
    },
    onError: (erro: Error) => toast.error(erro.message),
  });

  const suspender = useMutation({
    mutationFn: ({ memberId, reason }: { memberId: string; reason: string }) =>
      members.suspend(memberId, reason),
    onSuccess: () => {
      toast.success('Acesso suspenso. O vinculo e os papeis foram preservados.');
      setSuspendendo(null);
      setDetalhe(null);
      invalidar();
    },
    onError: (erro: Error) => {
      toast.error(erro.message);
      setSuspendendo(null);
    },
  });

  const inativar = useMutation({
    mutationFn: ({ memberId, reason }: { memberId: string; reason: string }) =>
      members.deactivate(memberId, reason),
    onSuccess: () => {
      toast.success('Usuario inativado. O vinculo e os perfis foram preservados.');
      setInativando(null);
      invalidar();
    },
    onError: (erro: Error) => {
      toast.error(erro.message);
      setInativando(null);
    },
  });

  const reativar = useMutation({
    mutationFn: (memberId: string) => members.reactivate(memberId),
    onSuccess: () => {
      toast.success('Acesso devolvido, com os mesmos papeis.');
      setDetalhe(null);
      setReativando(null);
      invalidar();
    },
    onError: (erro: Error) => {
      toast.error(erro.message);
      setReativando(null);
    },
  });

  const remover = useMutation({
    mutationFn: (memberId: string) => members.remove(memberId),
    onSuccess: () => {
      toast.success('Vinculo removido. A identidade da pessoa nao foi apagada.');
      setRemovendo(null);
      setDetalhe(null);
      invalidar();
    },
    onError: (erro: Error) => {
      toast.error(erro.message);
      setRemovendo(null);
    },
  });

  const mandarLink = useMutation({
    mutationFn: (memberId: string) => members.sendPasswordLink(memberId),
    onSuccess: (recibo) => {
      toast.success(
        recibo.delivered_by_email
          ? 'Link de redefinicao enviado ao e-mail da pessoa.'
          : 'Pedido registrado, mas o envio automatico de e-mail nao esta ligado. A pessoa pode pedir a recuperacao de senha na tela de acesso.',
      );
      setMandandoLink(null);
    },
    onError: (erro: Error) => {
      toast.error(erro.message);
      setMandandoLink(null);
    },
  });

  async function exportar() {
    setExportando(true);
    try {
      const arquivo = await members.export(filtros);
      const url = URL.createObjectURL(arquivo);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'usuarios.csv';
      link.click();
      URL.revokeObjectURL(url);
      toast.success('Lista de usuarios exportada.');
    } catch (erro) {
      toast.error(erro instanceof Error ? erro.message : 'Nao foi possivel exportar.');
    } finally {
      setExportando(false);
    }
  }

  function trocarAba(proxima: Aba) {
    setAba(proxima);
    if (daCorretora) return;
    // A aba de perfis tem endereco proprio, como no OnPoint (/usuarios/perfis).
    if (proxima === 'perfis' && !location.pathname.endsWith('/perfis')) {
      void navigate('/usuarios/perfis');
    } else if (proxima !== 'perfis' && location.pathname.endsWith('/perfis')) {
      void navigate('/usuarios');
    }
  }

  const titulo = daCorretora ? 'Usuarios da corretora' : 'Usuarios e perfis';

  if (!podeVer && !veQuadros) {
    return (
      <div className="flex flex-col gap-5">
        <PageHeader title={titulo} />
        <Alert tone="info" title="Indisponivel para o seu acesso">
          {daCorretora
            ? 'A consulta de usuarios da corretora e restrita ao administrador dela.'
            : 'A consulta de pessoas da empresa e restrita. Fale com quem administra.'}
        </Alert>
      </div>
    );
  }

  function acoesDe(member: Member): AcaoDoMenu[] {
    const souEu = member.user_id === session?.user.id;
    const ativo = member.status === 'active';
    const acoes: AcaoDoMenu[] = [
      {
        rotulo: 'Visualizar usuario',
        onSelect: () => setFormulario({ modo: 'visualizar', membro: member }),
      },
    ];
    if (podeEditar) {
      acoes.push({
        rotulo: 'Editar usuario',
        onSelect: () => setFormulario({ modo: 'editar', membro: member }),
      });
    }
    if (podeRemover && !souEu) {
      acoes.push({
        rotulo: 'Excluir usuario',
        perigo: true,
        onSelect: () => setRemovendo(member),
      });
    }
    if (podeEditar && !souEu) {
      acoes.push({
        rotulo: 'Ativar / inativar usuario',
        onSelect: () => (ativo ? setInativando(member) : setReativando(member)),
      });
    }
    // O que o SeguraCore ja tinha: perfis, corretora e suspensao com motivo.
    if (podeGerirPapeis || podeEditar || podeRemover) {
      acoes.push({ rotulo: 'Gerenciar acesso', onSelect: () => setDetalhe(member) });
    }
    if (podeMandarLink && !souEu && ativo) {
      acoes.push({
        rotulo: 'Enviar link de senha',
        onSelect: () => setMandandoLink(member),
      });
    }
    return acoes;
  }

  const colunas: Column<Member>[] = [
    {
      key: 'acoes',
      header: 'Acoes',
      render: (member) => (
        <MenuDeAcoes titulo={`Acoes de ${member.name}`} acoes={acoesDe(member)} />
      ),
    },
    {
      key: 'nome',
      header: 'Nome',
      render: (member) => (
        <p className="truncate font-medium text-ink-900">
          {member.name}
          {member.user_id === session?.user.id ? (
            <span className="ml-2 text-2xs font-normal text-ink-500">voce</span>
          ) : null}
        </p>
      ),
    },
    {
      key: 'email',
      header: 'E-mail',
      render: (member) => <span className="truncate text-ink-700">{member.email}</span>,
    },
    {
      key: 'telefone',
      header: 'Telefone',
      hideBelow: 'sm',
      render: (member) =>
        member.phone ? (
          <span className="whitespace-nowrap tabular-nums">
            {formatarTelefone(member.phone)}
          </span>
        ) : (
          <span className="text-ink-400">-</span>
        ),
    },
    {
      key: 'ativo',
      header: 'Ativo',
      render: (member) => (
        <div className="flex flex-col items-start gap-1">
          <span>{member.status === 'active' ? 'Sim' : 'Nao'}</span>
          {member.status !== 'active' ? (
            <StatusBadge kind="vinculo" value={member.status} />
          ) : null}
          {member.suspend_reason ? (
            <span className="max-w-40 truncate text-2xs text-ink-500">
              {member.suspend_reason}
            </span>
          ) : null}
        </div>
      ),
    },
    {
      key: 'perfil',
      header: 'Perfil',
      render: (member) => (
        <div className="flex flex-wrap gap-1.5">
          {member.roles.length === 0 ? (
            <span className="text-2xs text-ink-400">sem perfil</span>
          ) : (
            member.roles.map((role) => (
              <Badge key={role.id} tone={role.grants_all ? 'brand' : 'neutral'}>
                {role.name}
              </Badge>
            ))
          )}
        </div>
      ),
    },
    {
      key: 'acesso',
      header: 'Ultimo acesso',
      align: 'right',
      hideBelow: 'md',
      render: (member) =>
        member.last_login_at ? (
          formatDateTime(member.last_login_at)
        ) : (
          <span className="text-ink-400">nunca acessou</span>
        ),
    },
  ];

  const abas: { chave: Aba; rotulo: string; visivel: boolean }[] = [
    { chave: 'usuarios', rotulo: 'Usuarios', visivel: podeVer },
    // Os perfis da empresa, e a corretora nao os administra (a CORRETORA do OnPoint
    // tambem nao ve esta aba).
    { chave: 'perfis', rotulo: 'Perfis de acesso', visivel: veQuadros },
    // Quem enxerga a lista de pessoas enxerga tambem quem foi chamado e ainda nao
    // entrou: sao a mesma pergunta, e a mesma permissao.
    { chave: 'convites', rotulo: 'Convites', visivel: podeVer },
  ];
  const abaAtual = abas.find((item) => item.chave === aba && item.visivel)
    ? aba
    : (abas.find((item) => item.visivel)?.chave ?? 'usuarios');

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title={titulo}
        description={
          daCorretora
            ? 'Quem acessa o portal pela sua corretora e com qual perfil.'
            : 'Quem tem acesso a esta empresa e o que cada pessoa pode fazer.'
        }
      />

      <div role="tablist" aria-label="Usuarios e perfis" className={trilhoDeAbas}>
        {abas
          .filter((item) => item.visivel)
          .map((item) => (
            <button
              key={item.chave}
              type="button"
              role="tab"
              id={`aba-${item.chave}`}
              aria-selected={abaAtual === item.chave}
              aria-controls={`painel-${item.chave}`}
              onClick={() => trocarAba(item.chave)}
              className={
                abaAtual === item.chave ? classeDaAba(true) : classeDaAba(false)
              }
            >
              {item.rotulo}
            </button>
          ))}
      </div>

      {abaAtual === 'perfis' && veQuadros ? (
        <div role="tabpanel" id="painel-perfis" aria-labelledby="aba-perfis">
          <RoleBoards />
        </div>
      ) : null}

      {abaAtual === 'convites' ? (
        <div role="tabpanel" id="painel-convites" aria-labelledby="aba-convites">
          <InvitationsPanel papeis={listaPapeis.data ?? []} daCorretora={daCorretora} />
        </div>
      ) : null}

      {abaAtual === 'usuarios' ? (
        <div
          role="tabpanel"
          id="painel-usuarios"
          aria-labelledby="aba-usuarios"
          className="flex flex-col gap-4"
        >
          {daCorretora ? (
            <Alert tone="info" title="Usuarios da sua corretora">
              Quem voce cadastra entra ligado a sua corretora e enxerga so o que
              pertence a ela. O perfil Administrador da Corretora tambem cuida dos
              usuarios e dos dados da corretora; o Corretor Parceiro opera sem essas
              duas tarefas.
            </Alert>
          ) : null}

          {consulta.isError ? (
            <Alert tone="danger" title="Nao foi possivel carregar as pessoas">
              {consulta.error.message}
            </Alert>
          ) : null}

          <Card>
            <div className="flex flex-wrap items-end gap-3 border-b border-line px-4 py-3">
              <div className="min-w-56 flex-1">
                <Field
                  label="Buscar"
                  labelHidden
                  className="h-9"
                  placeholder="Digite para buscar..."
                  value={busca}
                  onChange={(evento) => setBusca(evento.target.value)}
                />
              </div>
              <div className="w-48">
                <Select
                  label="Situacao"
                  className="h-9"
                  value={situacao}
                  options={situacoes}
                  onChange={(evento) => setSituacao(evento.target.value)}
                />
              </div>
              {!daCorretora && corretoras.data ? (
                <div className="w-56">
                  <Select
                    label="Corretora"
                    className="h-9"
                    placeholder="Todas as corretoras"
                    value={corretora}
                    options={corretoras.data.items.map((item) => ({
                      value: item.id,
                      label: item.legal_name,
                    }))}
                    onChange={(evento) => setCorretora(evento.target.value)}
                  />
                </div>
              ) : null}
              <div className="ml-auto flex items-center gap-2 self-end">
                <span className="text-xs text-ink-600">
                  {consulta.data?.length ?? 0} pessoas
                </span>
                {podeExportar ? (
                  <Button
                    size="sm"
                    variant="secondary"
                    loading={exportando}
                    onClick={() => void exportar()}
                  >
                    Exportar CSV
                  </Button>
                ) : null}
                {podeCadastrar && podeGerirPapeis ? (
                  <Button size="sm" onClick={() => setFormulario({ modo: 'criar' })}>
                    Cadastrar Usuario
                  </Button>
                ) : null}
              </div>
            </div>

            <DataTable
              columns={colunas}
              items={consulta.data ?? []}
              keyOf={(member) => member.id}
              loading={consulta.isPending}
              onRowClick={(member) =>
                setFormulario({ modo: 'visualizar', membro: member })
              }
              empty={
                <EmptyState
                  icon="lock"
                  title={
                    buscaAplicada || situacao || corretora
                      ? 'Nenhuma pessoa encontrada'
                      : 'Nenhuma pessoa vinculada'
                  }
                  description={
                    buscaAplicada || situacao || corretora
                      ? 'Ajuste a busca ou os filtros para ver outras pessoas.'
                      : 'Cadastre um usuario para dar o primeiro acesso.'
                  }
                />
              }
            />
          </Card>
        </div>
      ) : null}

      {formulario?.modo === 'visualizar' && formulario.membro ? (
        <VisualizarUsuario
          membro={formulario.membro}
          papeis={listaPapeis.data ?? []}
          daCorretora={daCorretora}
          onClose={() => setFormulario(null)}
        />
      ) : null}

      {formulario && formulario.modo !== 'visualizar' ? (
        <UserFormModal
          modo={formulario.modo}
          membro={formulario.membro}
          papeis={listaPapeis.data ?? []}
          daCorretora={daCorretora}
          podeGerirPapeis={podeGerirPapeis}
          onClose={() => setFormulario(null)}
          onCriado={(resultado) => {
            setFormulario(null);
            setEmitido(resultado);
          }}
        />
      ) : null}

      {emitido ? (
        <EnderecoDoConvite emitido={emitido} onClose={() => setEmitido(null)} />
      ) : null}

      {detalhe ? (
        <DetalheDoVinculo
          membro={detalhe}
          papeis={listaPapeis.data ?? []}
          souEu={detalhe.user_id === session?.user.id}
          daCorretora={daCorretora}
          podeGerirPapeis={podeGerirPapeis}
          podeEditar={podeEditar}
          podeRemover={podeRemover}
          salvando={trocarPapeis.isPending || reativar.isPending}
          onClose={() => setDetalhe(null)}
          onSalvarPapeis={(roleIds) =>
            trocarPapeis.mutate({ memberId: detalhe.id, roleIds })
          }
          // A gaveta fecha ao abrir a confirmacao. Duas caixas de dialogo
          // empilhadas deixam a de tras alcancavel pelo teclado e anunciada pelo
          // leitor de tela, que passa a descrever uma tela que ninguem esta vendo.
          onSuspender={() => {
            setSuspendendo(detalhe);
            setDetalhe(null);
          }}
          onReativar={() => reativar.mutate(detalhe.id)}
          onRemover={() => {
            setRemovendo(detalhe);
            setDetalhe(null);
          }}
        />
      ) : null}

      {suspendendo ? (
        <FormularioDeSituacao
          membro={suspendendo}
          titulo="Suspender acesso"
          confirmar="Suspender"
          explicacao={`${suspendendo.name} perde o acesso na proxima requisicao, inclusive nas abas que ja estiverem abertas. O vinculo e os perfis ficam guardados, e reativar devolve os mesmos sem refazer o cadastro.`}
          salvando={suspender.isPending}
          onCancel={() => setSuspendendo(null)}
          onSubmit={(reason) => suspender.mutate({ memberId: suspendendo.id, reason })}
        />
      ) : null}

      {inativando ? (
        <FormularioDeSituacao
          membro={inativando}
          titulo="Inativar usuario"
          confirmar="Inativar"
          explicacao={`${inativando.name} passa a aparecer como Ativo: nao e perde o acesso na proxima requisicao. O vinculo e os perfis ficam guardados, e ativar de novo devolve os mesmos.`}
          salvando={inativar.isPending}
          onCancel={() => setInativando(null)}
          onSubmit={(reason) => inativar.mutate({ memberId: inativando.id, reason })}
        />
      ) : null}

      <ConfirmDialog
        open={reativando !== null}
        onClose={() => setReativando(null)}
        onConfirm={() => reativando && reativar.mutate(reativando.id)}
        title="Ativar usuario"
        message={
          reativando
            ? `${reativando.name} volta a ter acesso, com os mesmos perfis de antes.`
            : ''
        }
        confirmLabel="Ativar"
        loading={reativar.isPending}
      />

      <ConfirmDialog
        open={mandandoLink !== null}
        onClose={() => setMandandoLink(null)}
        onConfirm={() => mandandoLink && mandarLink.mutate(mandandoLink.id)}
        title="Enviar link de senha"
        message={
          mandandoLink
            ? `${mandandoLink.name} recebe no e-mail ${mandandoLink.email} um link para escolher uma senha nova. Ninguem que administra a empresa ve ou define a senha dela, e a senha atual continua valendo ate o link ser usado.`
            : ''
        }
        confirmLabel="Enviar link"
        loading={mandarLink.isPending}
      />

      <ConfirmDialog
        open={removendo !== null}
        onClose={() => setRemovendo(null)}
        onConfirm={() => removendo && remover.mutate(removendo.id)}
        title="Excluir usuario"
        message={
          removendo
            ? `${removendo.name} deixa de ter acesso a esta empresa. A identidade dela nao e apagada: o historico do que decidiu aqui continua apontando para ela, e o acesso a outras empresas nao muda. Para um afastamento temporario, inative ou suspenda em vez de excluir.`
            : ''
        }
        confirmLabel="Excluir"
        tone="danger"
        loading={remover.isPending}
      />
    </div>
  );
}

/**
 * Visualizar usuario. A leitura e pelo identificador (GET /members/{id}), e nao a
 * linha da lista: o que se ve e o que esta gravado agora.
 */
function VisualizarUsuario({
  membro,
  papeis,
  daCorretora,
  onClose,
}: {
  membro: Member;
  papeis: GrantableRole[];
  daCorretora: boolean;
  onClose: () => void;
}) {
  const leitura = useQuery({
    queryKey: ['members', 'um', membro.id],
    queryFn: ({ signal }) => members.get(membro.id, signal),
  });

  if (leitura.isPending) {
    return (
      <Modal open onClose={onClose} title="Visualizar usuario">
        <Skeleton className="h-40 w-full" />
      </Modal>
    );
  }
  return (
    <UserFormModal
      modo="visualizar"
      membro={leitura.data ?? membro}
      papeis={papeis}
      daCorretora={daCorretora}
      podeGerirPapeis={false}
      onClose={onClose}
    />
  );
}

/** Perfis, corretora e situacao do vinculo: o que o SeguraCore ja tinha. */
function DetalheDoVinculo({
  membro,
  papeis,
  souEu,
  daCorretora,
  podeGerirPapeis,
  podeEditar,
  podeRemover,
  salvando,
  onClose,
  onSalvarPapeis,
  onSuspender,
  onReativar,
  onRemover,
}: {
  membro: Member;
  papeis: GrantableRole[];
  souEu: boolean;
  daCorretora: boolean;
  podeGerirPapeis: boolean;
  podeEditar: boolean;
  podeRemover: boolean;
  salvando: boolean;
  onClose: () => void;
  onSalvarPapeis: (roleIds: string[]) => void;
  onSuspender: () => void;
  onReativar: () => void;
  onRemover: () => void;
}) {
  const [selecionados, setSelecionados] = useState<string[]>(
    membro.roles.map((role) => role.id),
  );

  const afastado = membro.status !== 'active';
  const mudou =
    selecionados.length !== membro.roles.length ||
    selecionados.some((id) => !membro.roles.some((role) => role.id === id));
  const oferecidos = papeis.filter(
    (papel) => papel.is_assignable !== false || selecionados.includes(papel.id),
  );

  function alternar(roleId: string) {
    setSelecionados((atual) =>
      atual.includes(roleId) ? atual.filter((id) => id !== roleId) : [...atual, roleId],
    );
  }

  return (
    <Modal open onClose={onClose} title={membro.name} description={membro.email}>
      <div className="flex flex-col gap-5">
        <dl className="grid grid-cols-2 gap-3 text-xs">
          <div>
            <dt className="text-ink-500">Situacao</dt>
            <dd className="mt-1">
              <StatusBadge kind="vinculo" value={membro.status} />
            </dd>
          </div>
          <div>
            <dt className="text-ink-500">Ultimo acesso</dt>
            <dd className="mt-1 text-ink-800">
              {membro.last_login_at
                ? formatDateTime(membro.last_login_at)
                : 'nunca acessou'}
            </dd>
          </div>
        </dl>

        {afastado && membro.suspend_reason ? (
          <Alert tone="warning" title="Acesso afastado">
            {membro.suspend_reason}
          </Alert>
        ) : null}

        <section className="border-t border-line pt-4">
          <h3 className="text-xs font-semibold text-ink-700">Perfis de acesso</h3>
          {!podeGerirPapeis ? (
            <div className="mt-2 flex flex-wrap gap-1.5">
              {membro.roles.length === 0 ? (
                <span className="text-sm text-ink-500">Sem perfil concedido.</span>
              ) : (
                membro.roles.map((role) => (
                  <Badge key={role.id} tone={role.grants_all ? 'brand' : 'neutral'}>
                    {role.name}
                  </Badge>
                ))
              )}
            </div>
          ) : (
            <>
              <p className="mt-1 text-2xs text-ink-500">
                Marque os perfis desta pessoa. A mudanca vale na proxima requisicao
                dela.
              </p>
              <ul className="mt-2 flex max-h-64 flex-col divide-y divide-line overflow-y-auto rounded-md border border-line">
                {oferecidos.map((role) => (
                  <li key={role.id}>
                    <label className="flex cursor-pointer items-start gap-2.5 px-3 py-2.5 hover:bg-ink-50">
                      <input
                        type="checkbox"
                        className="mt-0.5 size-4 accent-[var(--color-brand-500)]"
                        checked={selecionados.includes(role.id)}
                        onChange={() => alternar(role.id)}
                      />
                      <span className="min-w-0">
                        <span className="block text-sm font-medium text-ink-800">
                          {role.name}
                        </span>
                        <span className="block text-2xs text-ink-500">
                          {role.grants_all
                            ? 'Concede todas as permissoes do catalogo.'
                            : role.description}
                        </span>
                      </span>
                    </label>
                  </li>
                ))}
              </ul>

              {selecionados.length === 0 ? (
                <p className="mt-2 text-2xs text-amber-700">
                  Sem perfil, a pessoa entra na plataforma e nao consegue executar
                  nenhuma acao.
                </p>
              ) : null}

              <div className="mt-3 flex justify-end">
                <Button
                  size="sm"
                  disabled={!mudou}
                  loading={salvando}
                  onClick={() => onSalvarPapeis(selecionados)}
                >
                  Salvar perfis
                </Button>
              </div>
            </>
          )}
        </section>

        {/* A corretora da pessoa e decisao da seguradora. Quem administra a
            corretora nao a escolhe: a pessoa ja pertence a corretora da sessao. */}
        {!daCorretora &&
        podeGerirPapeis &&
        membro.roles.some((role) => role.broker_scoped) ? (
          <CorretoraDoVinculo membro={membro} />
        ) : null}

        {podeEditar || podeRemover ? (
          <section className="flex flex-col gap-2 border-t border-line pt-4">
            <h3 className="text-xs font-semibold text-ink-700">Acesso</h3>
            {souEu ? (
              <p className="text-2xs text-ink-500">
                Voce nao pode suspender nem remover o proprio acesso. Peca a outra
                pessoa que administra a empresa.
              </p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {podeEditar && afastado ? (
                  <Button size="sm" variant="secondary" onClick={onReativar}>
                    Reativar acesso
                  </Button>
                ) : null}
                {podeEditar && !afastado ? (
                  <Button size="sm" variant="secondary" onClick={onSuspender}>
                    Suspender acesso
                  </Button>
                ) : null}
                {podeRemover ? (
                  <Button size="sm" variant="danger" onClick={onRemover}>
                    Remover vinculo
                  </Button>
                ) : null}
              </div>
            )}
            <p className="text-2xs text-ink-500">
              Suspender preserva o vinculo e os perfis, e reativar devolve os mesmos.
              Remover desfaz o vinculo com esta empresa e nao apaga a identidade da
              pessoa.
            </p>
          </section>
        ) : null}
      </div>
    </Modal>
  );
}

/**
 * Corretora que a pessoa representa.
 *
 * O perfil com escopo de corretora so mostra o que pertence a corretora ligada ao
 * vinculo. Sem ligacao, a pessoa entra e nao enxerga registro nenhum: a regra fecha
 * em vez de abrir, e por isso o aviso aparece ate a corretora ser escolhida.
 */
function CorretoraDoVinculo({ membro }: { membro: Member }) {
  const { can } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [corretora, setCorretora] = useState(membro.broker_id ?? '');

  const listaCorretoras = useQuery({
    queryKey: ['brokers', 'options'],
    queryFn: ({ signal }) => brokers.list({ limit: 100, stage: 'ativo' }, signal),
    enabled: can('corretores.corretor.view'),
  });

  const salvar = useMutation({
    mutationFn: () => members.setBroker(membro.id, corretora === '' ? null : corretora),
    onSuccess: () => {
      toast.success('Corretora atualizada. Vale na proxima requisicao da pessoa.');
      void queryClient.invalidateQueries({ queryKey: ['members'] });
    },
    onError: (erro: Error) => toast.error(erro.message),
  });

  return (
    <section className="flex flex-col gap-2 border-t border-line pt-4">
      <h3 className="text-xs font-semibold text-ink-700">Corretora representada</h3>
      <Select
        label="Corretora"
        placeholder="Nenhuma corretora"
        value={corretora}
        options={(listaCorretoras.data?.items ?? []).map((broker) => ({
          value: broker.id,
          label: broker.legal_name,
        }))}
        hint="O perfil desta pessoa mostra apenas o que pertence a corretora escolhida."
        onChange={(event) => setCorretora(event.target.value)}
      />
      {corretora === '' ? (
        <p className="text-2xs text-amber-700">
          Sem corretora, esta pessoa nao enxerga nenhuma operacao, apolice, comissao ou
          tomador.
        </p>
      ) : null}
      <div className="flex justify-end">
        <Button
          size="sm"
          disabled={corretora === (membro.broker_id ?? '')}
          loading={salvar.isPending}
          onClick={() => salvar.mutate()}
        >
          Salvar corretora
        </Button>
      </div>
    </section>
  );
}

/** Suspender ou inativar, com o motivo opcional que fica na trilha. */
function FormularioDeSituacao({
  membro,
  titulo,
  confirmar,
  explicacao,
  salvando,
  onCancel,
  onSubmit,
}: {
  membro: Member;
  titulo: string;
  confirmar: string;
  explicacao: string;
  salvando: boolean;
  onCancel: () => void;
  onSubmit: (reason: string) => void;
}) {
  const [motivo, setMotivo] = useState('');

  return (
    <Modal
      open
      onClose={onCancel}
      title={titulo}
      description={membro.name}
      footer={
        <>
          <Button variant="secondary" onClick={onCancel}>
            Cancelar
          </Button>
          <Button variant="danger" loading={salvando} onClick={() => onSubmit(motivo)}>
            {confirmar}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <p className="text-sm text-ink-700">{explicacao}</p>
        <Textarea
          label="Motivo"
          value={motivo}
          hint="Opcional. Aparece na lista e fica na trilha de auditoria."
          onChange={(evento) => setMotivo(evento.target.value)}
        />
      </div>
    </Modal>
  );
}
